import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";
import "./DashboardMaestro.css";

const PLOTLY_CDN = "https://cdn.plot.ly/plotly-2.35.2.min.js";

let plotlyLoader = null;
function loadPlotly() {
  if (window.Plotly) return Promise.resolve(window.Plotly);
  if (plotlyLoader) return plotlyLoader;
  plotlyLoader = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = PLOTLY_CDN;
    s.async = true;
    s.onload = () => resolve(window.Plotly);
    s.onerror = () => reject(new Error("Impossible de charger Plotly"));
    document.head.appendChild(s);
  });
  return plotlyLoader;
}

function tcIndex(name) {
  const m = String(name).match(/_(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

function tcColor(tcname, colors) {
  const idx = tcIndex(tcname);
  if (idx == null) return "#888888";
  return colors[(idx - 1) % colors.length];
}

function timeTicks(maxSeconds) {
  if (!maxSeconds || maxSeconds <= 0) return { tickvals: [0], ticktext: ["0h00"] };
  const stepsMin = [5, 10, 15, 20, 30, 60, 90, 120, 180, 240, 360, 480, 720, 1440, 2880, 4320];
  const maxMin = maxSeconds / 60;
  let step = stepsMin[stepsMin.length - 1];
  for (let k = 0; k < stepsMin.length; k += 1) {
    if (maxMin / stepsMin[k] <= 7) {
      step = stepsMin[k];
      break;
    }
  }
  const stepSec = step * 60;
  const tickvals = [];
  const ticktext = [];
  for (let t = 0; t <= maxSeconds + stepSec * 0.01; t += stepSec) {
    const totalMin = Math.round(t / 60);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    tickvals.push(t);
    ticktext.push(`${h}h${String(m).padStart(2, "0")}`);
  }
  return { tickvals, ticktext };
}

function buildThermoPlot(rec, cfg, height = 200) {
  const traces = [];
  const flatSet = new Set(rec.flatChannels || []);
  const tcNames = Object.keys(rec.thermocouples || {}).sort(
    (a, b) => (tcIndex(a) || 0) - (tcIndex(b) || 0)
  );

  tcNames.forEach((tcname) => {
    const isFlat = flatSet.has(tcname);
    const color = isFlat ? cfg.flatColor : tcColor(tcname, cfg.tcColors);
    traces.push({
      x: rec.x,
      y: rec.thermocouples[tcname],
      mode: "lines",
      type: "scatter",
      line: { color, width: isFlat ? 1 : 1.6, dash: isFlat ? "dot" : "solid" },
      showlegend: false,
      hovertemplate: `${tcname} — %{y:.1f} °C<extra></extra>`,
    });
  });

  const maxX = rec.x?.length ? rec.x[rec.x.length - 1] : 0;
  const tt = timeTicks(maxX);

  return {
    traces,
    layout: {
      margin: { l: 42, r: 16, t: 8, b: 28 },
      height,
      xaxis: {
        tickvals: tt.tickvals,
        ticktext: tt.ticktext,
        tickfont: { size: 9, color: "#9a9a92" },
        gridcolor: "rgba(255,255,255,0.06)",
        zeroline: false,
        fixedrange: false,
      },
      yaxis: {
        tickfont: { size: 9, color: "#9a9a92" },
        gridcolor: "rgba(255,255,255,0.06)",
        zeroline: false,
        fixedrange: false,
        title: { text: "°C", font: { size: 9, color: "#9a9a92" } },
      },
      paper_bgcolor: "rgba(0,0,0,0)",
      plot_bgcolor: "rgba(255,255,255,0.02)",
      font: { color: "#9a9a92" },
      dragmode: "zoom",
    },
    config: {
      responsive: true,
      scrollZoom: true,
      displayModeBar: true,
      modeBarButtonsToRemove: ["lasso2d", "select2d", "autoScale2d"],
      displaylogo: false,
    },
  };
}

function ThermoCurveChart({ rec, cfg, height = 200, compact = true }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !rec?.x?.length) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const Plotly = await loadPlotly();
        if (cancelled || !ref.current) return;
        const { traces, layout, config } = buildThermoPlot(rec, cfg, height);
        await Plotly.newPlot(el, traces, layout, {
          ...config,
          displayModeBar: !compact,
        });
      } catch {
        if (ref.current) {
          ref.current.innerHTML = '<div class="maestro-empty">Graphique indisponible</div>';
        }
      }
    })();

    return () => {
      cancelled = true;
      if (window.Plotly && ref.current) {
        window.Plotly.purge(ref.current);
      }
    };
  }, [rec, cfg, height, compact]);

  return <div ref={ref} className="maestro-chart" />;
}

export default function DashboardMaestroThermocouples() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [records, setRecords] = useState([]);
  const [cfg, setCfg] = useState({
    defaultShow: "24",
    showOptions: ["12", "24", "48", "96", "tous"],
    tcColors: [],
    flatColor: "#B7B5AC",
    tcNames: [],
  });
  const [generatedAt, setGeneratedAt] = useState("");
  const [showCount, setShowCount] = useState("24");
  const [stackFilter, setStackFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/dashboard/maestro/thermocouples");
      const data = await res.json();
      if (!res.ok || data.status !== "success") {
        throw new Error(data.message || `Erreur HTTP ${res.status}`);
      }
      setRecords(data.records || []);
      if (data.cfg) {
        setCfg((c) => ({ ...c, ...data.cfg }));
        setShowCount(data.cfg.defaultShow || "24");
      }
      setGeneratedAt(data.generatedAt || "");
    } catch (e) {
      setError(e.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stackOptions = useMemo(
    () => [...records].sort((a, b) => String(a.id).localeCompare(String(b.id))),
    [records]
  );

  const filtered = useMemo(() => {
    let list = records;
    if (stackFilter !== "all") {
      list = list.filter((r) => r.id === stackFilter);
    }
    const q = search.trim().toUpperCase();
    if (q) {
      list = list.filter((r) => String(r.id).toUpperCase().includes(q));
    }
    if (showCount !== "tous") {
      const n = parseInt(showCount, 10);
      if (!Number.isNaN(n)) list = list.slice(0, n);
    }
    return list;
  }, [records, stackFilter, search, showCount]);

  const legendTcs = cfg.tcNames?.length
    ? cfg.tcNames
    : [...new Set(records.flatMap((r) => Object.keys(r.thermocouples || {})))].sort(
        (a, b) => (tcIndex(a) || 0) - (tcIndex(b) || 0)
      );

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setExpanded(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  return (
    <div className="maestro-page">
      <MenuBar active="dashboard" />
      <div className="maestro-wrap">
        <header className="maestro-hero">
          <div>
            <p className="maestro-kicker">Dashboard · Fabrication</p>
            <h1 className="maestro-title">Maestro — thermocouples</h1>
            <p className="maestro-sub">
              PressTC1…PressTC6 par IDS (MaestroCurves)
              {generatedAt ? ` · ${generatedAt}` : ""}
            </p>
          </div>
          <button type="button" className="maestro-back" onClick={() => navigate(-1)}>
            Retour
          </button>
        </header>

        <div className="maestro-legend">
          {legendTcs.map((tcname) => (
            <span key={tcname} className="maestro-legend-item">
              <span
                className="maestro-dot"
                style={{ background: tcColor(tcname, cfg.tcColors) }}
              />
              {tcname}
            </span>
          ))}
          <span className="maestro-legend-item">
            <span className="maestro-dash" />
            Voie à plat (non branchée ?)
          </span>
        </div>

        <div className="maestro-bar">
          <div className="maestro-grp">
            <span className="maestro-lbl">Afficher</span>
            <select
              className="maestro-sel"
              value={showCount}
              onChange={(e) => setShowCount(e.target.value)}
            >
              {(cfg.showOptions || ["12", "24", "48", "96", "tous"]).map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div className="maestro-grp">
            <span className="maestro-lbl">IDS</span>
            <select
              className="maestro-sel"
              style={{ minWidth: 180 }}
              value={stackFilter}
              onChange={(e) => setStackFilter(e.target.value)}
            >
              <option value="all">Tous</option>
              {stackOptions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.id}
                </option>
              ))}
            </select>
          </div>
          <div className="maestro-grp">
            <span className="maestro-lbl">Recherche</span>
            <input
              className="maestro-inp"
              placeholder="IDS101523…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button type="button" className="maestro-btn-sm" onClick={loadData}>
            Actualiser
          </button>
          <span className="maestro-hint">Molette = zoom · glisser = sélection · double-clic = reset</span>
        </div>

        <div className="maestro-grid">
          {loading && <div className="maestro-loading">Chargement des thermocouples…</div>}
          {error && !loading && <div className="maestro-error">{error}</div>}
          {!loading && !error && filtered.length === 0 && (
            <div className="maestro-empty">Aucun IDS avec thermocouples dans MaestroCurves.</div>
          )}
          {!loading &&
            !error &&
            filtered.map((rec) => (
              <article key={rec.id} className="maestro-card">
                <div className="maestro-card-hd">
                  <span className="maestro-cid">{rec.id}</span>
                  {rec.equipment && <span className="maestro-badge">{rec.equipment}</span>}
                </div>
                <div className="maestro-meta">
                  <span>{rec.date}</span>
                  <span>Durée {rec.durationTxt}</span>
                </div>
                {rec.recipe && (
                  <div className="maestro-meta">
                    <span>Recette {rec.recipe}</span>
                  </div>
                )}
                {rec.flatChannels?.length > 0 && (
                  <div className="maestro-meta" style={{ color: "#f87171" }}>
                    <span>À plat : {rec.flatChannels.join(", ")}</span>
                  </div>
                )}
                <div
                  className="maestro-gbox"
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpanded(rec)}
                  onKeyDown={(e) => e.key === "Enter" && setExpanded(rec)}
                  title="Cliquer pour agrandir et zoomer"
                >
                  <ThermoCurveChart rec={rec} cfg={cfg} height={200} compact />
                </div>
                <div className="maestro-card-actions">
                  <button type="button" className="maestro-btn-sm" onClick={() => setExpanded(rec)}>
                    Agrandir / Zoom
                  </button>
                </div>
              </article>
            ))}
        </div>
      </div>

      {expanded && (
        <div
          className="maestro-modal-overlay"
          role="presentation"
          onClick={() => setExpanded(null)}
        >
          <div
            className="maestro-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="maestro-modal-hd">
              <div>
                <div className="maestro-modal-title">{expanded.id}</div>
                <div className="maestro-modal-meta">
                  {expanded.date} · Durée {expanded.durationTxt}
                  {expanded.recipe ? ` · Recette ${expanded.recipe}` : ""}
                </div>
              </div>
              <button type="button" className="maestro-modal-close" onClick={() => setExpanded(null)}>
                ×
              </button>
            </div>
            <div className="maestro-modal-body">
              <ThermoCurveChart rec={expanded} cfg={cfg} height={520} compact={false} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
